import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PackageStatusBadge from './PackageStatusBadge';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import PackageVersionPicker from './PackageVersionPicker';
import PackageVersionActions from './PackageVersionActions';
import { packageTypeLabel } from './packageConstants';
import routes from '../../../Constants/routes';
import type { VossVersionStatus } from '../../../Constants/VOSS/vossVersionStatus';
import type { PackageDetail } from './Packages.types';
import type { NumberedVersion } from './versionUtils';

interface PackageDetailHeaderProps {
    vossPackage: PackageDetail;
    versions: NumberedVersion[];
    selectedVersionId: string | undefined;
    onSelectVersion: (versionId: string) => void;
    onShowHistory: () => void;
    onCreateVersion: () => void;
    onChangeStatus: (target: VossVersionStatus) => void;
}

const PackageDetailHeader = ({ vossPackage, versions, selectedVersionId, onSelectVersion, onShowHistory, onCreateVersion, onChangeStatus }: PackageDetailHeaderProps) => {
    const { t } = useTranslation();

    const externalId = vossPackage.externalId ?? '';
    const targetGroups = (vossPackage.targetGroups ?? []).map((group) => group.name).filter(Boolean);
    const type = packageTypeLabel(vossPackage.type);

    return (
        <>
            <div className="mb-16">
                <Link to={routes.vossPackages}>← {t('packages.title')}</Link>
                <span className="text-secondary mx-8">›</span>
                <span className="text-secondary">{vossPackage.name}</span>
            </div>

            <div className="d-flex align-items-center gap-16 mb-8">
                <h1 className="mb-0 flex-shrink-0">{vossPackage.name}</h1>
                <PackageStatusBadge status={vossPackage.status} />
                <div className="d-flex align-items-center gap-8 ml-auto flex-wrap justify-content-end">
                    <PackageVersionPicker versions={versions} selectedVersionId={selectedVersionId} onSelectVersion={onSelectVersion} />
                    {versions.length > 0 && (
                        <PackageVersionActions currentStatus={vossPackage.status} onShowHistory={onShowHistory} onCreateVersion={onCreateVersion} onChangeStatus={onChangeStatus} />
                    )}
                </div>
            </div>

            <div className="d-flex flex-wrap gap-16 text-secondary font-13 mb-24">
                <span className="d-inline-flex align-items-center gap-4">
                    <SpirisIcon name="tag" size="sm" />
                    {externalId.length > 0 ? externalId : t('packages.noExternalId')}
                </span>
                {type.length > 0 && (
                    <span className="d-inline-flex align-items-center gap-4">
                        <SpirisIcon name="briefcase-business" size="sm" />
                        {type}
                    </span>
                )}
                {targetGroups.length > 0 && (
                    <span className="d-inline-flex align-items-center gap-4">
                        <SpirisIcon name="users" size="sm" />
                        {targetGroups.join(', ')}
                    </span>
                )}
            </div>
        </>
    );
};

export default PackageDetailHeader;
